import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { memo, useMemo } from "react";
import { Chart } from "react-chartjs-2";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import {
  defaultDateFormatter,
  defaultValueFormatter,
  getChartOptions,
} from "./chart-options";
import { crosshairPlugin } from "./crosshair-plugin";
import * as redoLineAndBarChartCss from "./redo-line-and-bar-chart.module.css";
import {
  ChartSeries,
  RedoChartLegendPosition,
  RedoMixedBarAndLineChartProps,
  SeriesType,
} from "./types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  crosshairPlugin,
);

const ChartLegendItem = memo(function ChartLegendItem({
  name,
  color,
  type,
}: {
  name: string;
  color: string;
  type: SeriesType;
}) {
  return (
    <Flex align="center" gap="xs">
      <div
        className={redoLineAndBarChartCss.legendIndicator}
        style={{
          backgroundColor: type === "bar" ? color : "transparent",
          borderColor: color,
          borderWidth: type === "line" ? 2 : 0,
          borderStyle: "solid",
        }}
      />
      <Text fontSize="sm" textColor="tertiary" title={name}>
        {name}
      </Text>
    </Flex>
  );
});

const ChartLegend = memo(function ChartLegend({
  series,
  position,
}: {
  series: (ChartSeries & { type: SeriesType })[];
  position: RedoChartLegendPosition;
}) {
  if (!position || !series.length) {
    return null;
  }

  return (
    <Flex
      className={position === "right" ? redoLineAndBarChartCss.legendRight : ""}
      flexDirection={
        position === "top" || position === "bottom" ? "row" : "column"
      }
      gap="md"
      justify={position === "bottom" ? "center" : "flex-start"}
      wrap={position === "top" || position === "bottom" ? "wrap" : "nowrap"}
    >
      {series.map((item, index) => (
        <ChartLegendItem
          color={item.color || ""}
          key={index}
          name={item.name}
          type={item.type}
        />
      ))}
    </Flex>
  );
});

export const RedoMixedBarAndLineChart = memo(function RedoMixedBarAndLineChart({
  series,
  leftAxisLabel,
  rightAxisLabel,
  bottomAxisLabel,
  legendPosition,
  dateFormatter = defaultDateFormatter,
  valueAxisFormatter = defaultValueFormatter,
  rightAxisFormatter,
  valueTooltipFormatter = defaultValueFormatter,
  titleText,
  showTooltipSeriesNames = false,
  customTooltip,
  leftAxisMaxMultiplier,
  rightAxisMaxMultiplier,
  rightAxisPrecision,
  leftAxisMax,
  rightAxisMax,
}: RedoMixedBarAndLineChartProps) {
  const { processedData, normalizedSeriesWithTypes } = useMemo(() => {
    // Create a unified date map
    const dateMap = new Map<
      string,
      { formatted: string; instant: Temporal.Instant }
    >();

    for (const s of series) {
      for (const { date } of s.data) {
        const key = date.toString();
        if (!dateMap.has(key)) {
          dateMap.set(key, { formatted: dateFormatter(date), instant: date });
        }
      }
    }

    const dateEntries = [...dateMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => ({ key, ...val }));

    const labels = dateEntries.map((d) => d.formatted);

    // Normalize series data
    const normalizedSeries = series.map((s) => {
      const valueByDate = new Map(
        s.data.map((d) => [d.date.toString(), d.value]),
      );

      const normalizedData = dateEntries.map((d) => ({
        date: d.instant,
        value: valueByDate.get(d.key) ?? 0,
      }));

      return { ...s, data: normalizedData };
    });

    // Create datasets for Chart.js
    const datasets = normalizedSeries.map((s) => {
      const isLine = s.type === "line";
      const shouldFill = isLine && s.fill === true;

      return {
        type: s.type as "line" | "bar",
        label: s.name,
        data: s.data.map((d) => d.value),
        yAxisID: s.yAxisId || "y", // Use specified yAxisId or default to 'y'
        backgroundColor: isLine
          ? `${s.color}20` // 20% opacity for line fill
          : s.color,
        hoverBackgroundColor:
          !isLine && s.hoverColor ? s.hoverColor : undefined,
        borderColor: s.color,
        borderWidth: isLine ? 2 : 0,
        tension: isLine ? 0.4 : undefined,
        pointRadius: isLine ? 0 : undefined,
        pointHoverRadius: isLine ? 4 : undefined,
        fill: shouldFill,
        borderRadius: !isLine ? 4 : undefined,
        order: isLine ? 1 : 2, // Lines on top of bars
        barPercentage: !isLine && s.barPercentage ? s.barPercentage : undefined,
      };
    });

    const chartData: ChartData<"bar" | "line"> = { labels, datasets };

    return {
      processedData: chartData,
      normalizedSeriesWithTypes: normalizedSeries,
    };
  }, [series, dateFormatter]);

  const options: ChartOptions<"bar" | "line"> = useMemo(() => {
    const baseOptions = getChartOptions({
      leftAxisLabel,
      rightAxisLabel,
      bottomAxisLabel,
      valueAxisFormatter,
      rightAxisFormatter,
      valueTooltipFormatter,
      chartStyle: "bar", // Use bar style to enable x-axis offset for edge bars
      showSeriesNames: showTooltipSeriesNames,
      customTooltip,
      series: normalizedSeriesWithTypes,
      leftAxisMaxMultiplier,
      rightAxisMaxMultiplier,
      rightAxisPrecision,
      leftAxisMax,
      rightAxisMax,
    });

    return {
      ...baseOptions,
      maintainAspectRatio: false,
      responsive: true,
      interaction: { mode: "index", intersect: false },
    };
  }, [
    leftAxisLabel,
    rightAxisLabel,
    bottomAxisLabel,
    valueAxisFormatter,
    rightAxisFormatter,
    valueTooltipFormatter,
    showTooltipSeriesNames,
    customTooltip,
    normalizedSeriesWithTypes,
    leftAxisMaxMultiplier,
    rightAxisMaxMultiplier,
    rightAxisPrecision,
    leftAxisMax,
    rightAxisMax,
  ]);

  return (
    <Flex dir="column" height="full" w="full">
      {(titleText || legendPosition === "top") && (
        <Flex
          align="center"
          justify={titleText ? "space-between" : "flex-end"}
          mb="md"
          w="full"
        >
          {titleText && (
            <Text fontSize="sm" fontWeight="medium" textColor="tertiary">
              {titleText}
            </Text>
          )}

          {legendPosition === "top" && (
            <ChartLegend
              position={legendPosition}
              series={normalizedSeriesWithTypes}
            />
          )}
        </Flex>
      )}

      <Flex grow={1} height="full" w="full">
        <Flex
          className={redoLineAndBarChartCss.chartContainer}
          height="full"
          minW="none"
          position="relative"
          w="full"
        >
          <Chart data={processedData} options={options} type="bar" />
        </Flex>

        {legendPosition === "right" && (
          <ChartLegend
            position={legendPosition}
            series={normalizedSeriesWithTypes}
          />
        )}
      </Flex>

      {legendPosition === "bottom" && (
        <ChartLegend
          position={legendPosition}
          series={normalizedSeriesWithTypes}
        />
      )}
    </Flex>
  );
});
